import React, { useCallback, useEffect, useState } from "react";
import { createBuyBox, getBuyBoxes } from "../api";
import { BuyBox } from "../types";
import { MapBuyBox, mapBuyBoxActiveCount, mapBuyBoxFromSaved, mapBuyBoxToInput } from "../lib/mapBuyBox";
import { capture } from "../lib/analytics";

// Saved buy boxes on the map tab: load one into the map's buy box, or save
// the map's criteria as a new one. These are the same boxes as the Leads
// tab's "Saved" row (and the email alerts), so a box saved here works there
// and back. Paid plans only, like the buy-box API; the parent renders this
// only for them.

interface Props {
  current: MapBuyBox;
  countyId: string;
  countyName?: string;
  onApply: (box: MapBuyBox) => void;
  /** Max saved boxes for the plan; omit for unlimited. */
  savedLimit?: number;
}

const MapSavedBuyBoxes: React.FC<Props> = ({ current, countyId, countyName, onApply, savedLimit }) => {
  const [boxes, setBoxes] = useState<BuyBox[]>([]);
  const [selectedId, setSelectedId] = useState("");
  const [notApplied, setNotApplied] = useState<string[]>([]);
  const [saveMode, setSaveMode] = useState(false);
  const [saveName, setSaveName] = useState("");
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");

  const load = useCallback(async () => {
    try {
      setBoxes(await getBuyBoxes());
    } catch {
      setBoxes([]); // 403 for a plan without buy boxes, or transient
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleLoad = (id: string) => {
    setSelectedId(id);
    setMessage("");
    const saved = boxes.find((b) => b.id === id);
    if (!saved) {
      setNotApplied([]);
      return;
    }
    const { box, notApplied: skipped } = mapBuyBoxFromSaved(saved, countyId);
    setNotApplied(skipped);
    onApply(box);
    capture("map_buybox_loaded", { criteria_count: mapBuyBoxActiveCount(box), not_applied: skipped.length });
  };

  const atLimit = savedLimit !== undefined && boxes.length >= savedLimit;
  const canSave = mapBuyBoxActiveCount(current) > 0 && !atLimit;

  const handleSave = async () => {
    const name = saveName.trim() || "My buy box";
    setSaving(true);
    setMessage("");
    try {
      const created = await createBuyBox(mapBuyBoxToInput(name, current, countyId));
      await load();
      setSelectedId(created.id);
      setNotApplied([]);
      setSaveMode(false);
      setMessage(`Saved${countyName ? ` for ${countyName}` : ""}. It's in Leads too.`);
      capture("map_buybox_saved", { criteria_count: mapBuyBoxActiveCount(current) });
    } catch (err: any) {
      setMessage(
        err?.response?.data?.code === "buy_box_cap_reached"
          ? `You've saved ${err.response.data.limit ?? savedLimit ?? "the maximum"} searches, the most your plan allows.`
          : "Couldn't save this buy box. Please try again."
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="map-buybox-saved">
      <div className="map-buybox-row">
        <span className="map-buybox-label">Saved</span>
        <select
          className="lv-chip"
          value={selectedId}
          onChange={(e) => handleLoad(e.target.value)}
          aria-label="Load a saved buy box on the map"
        >
          <option value="">— Buy box —</option>
          {boxes.map((b) => (
            <option key={b.id} value={b.id}>
              {b.name}
            </option>
          ))}
        </select>
        {!saveMode && (
          <button
            type="button"
            className="lv-chip"
            disabled={!canSave}
            onClick={() => {
              setSaveName("");
              setSaveMode(true);
            }}
            title={atLimit ? `You've used all ${savedLimit} saved searches.` : "Save these criteria as a buy box"}
          >
            Save as…
          </button>
        )}
      </div>
      {saveMode && (
        <div className="map-buybox-row">
          <input
            className="bbx-save-input"
            autoFocus
            value={saveName}
            onChange={(e) => setSaveName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleSave();
              if (e.key === "Escape") setSaveMode(false);
            }}
            placeholder="Name this buy box"
            aria-label="Buy box name"
            disabled={saving}
          />
          <button type="button" className="lv-chip" onClick={handleSave} disabled={saving}>
            {saving ? "Saving…" : "Save"}
          </button>
          <button
            type="button"
            className="bbx-save-x"
            onClick={() => setSaveMode(false)}
            disabled={saving}
            aria-label="Cancel"
          >
            ×
          </button>
        </div>
      )}
      {notApplied.length > 0 && (
        <p className="map-buybox-note" role="note">
          The map doesn't apply this box's {notApplied.join(", ")}. Leads and email alerts do.
        </p>
      )}
      {message && (
        <p className="map-buybox-note" role="status">
          {message}
        </p>
      )}
    </div>
  );
};

export default MapSavedBuyBoxes;
